import { useState } from "react";
import { motion } from "framer-motion";
import { UploadCloud, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { MaskLines } from "../components/Motion";
import { api, errorText } from "../lib/api";

const CATS = ["Strategy", "Finance", "Leadership", "Operations", "Technology", "Macroeconomics"];
const TYPES = ["Research article", "Practitioner essay", "Case study"];

function Field({ label, children }) {
  return <label className="block"><span className="eyebrow">{label}</span>{children}</label>;
}

export default function Submit() {
  const [f, setF] = useState({ title: "", authors: "", email: "", affiliation: "", orcid: "", abstract: "", keywords: "", category: "Strategy", manuscript_type: "Research article" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (f.abstract.trim().length < 50) { toast.error("Abstract should be at least 50 characters."); return; }
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("file", file);
    try { const { data } = await api.post("/submissions", fd); setRef(data?.reference || "received"); }
    catch (err) { toast.error(errorText(err)); }
    setBusy(false);
  };

  if (ref) return (
    <div className="px-5 lg:px-12 py-32 max-w-3xl mx-auto text-center" data-testid="submission-success">
      <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}><CheckCircle2 size={56} className="mx-auto text-[var(--red)]" /></motion.div>
      <h1 className="f-serif text-5xl mt-8">Manuscript received.</h1>
      <p className="text-lg text-[var(--ink-2)] mt-5">Your reference is <span className="f-mono text-[var(--ink)]" data-testid="submission-reference">{ref}</span>. The editorial office will confirm next steps by email.</p>
    </div>
  );

  return (
    <div className="px-5 lg:px-12 pt-14" data-testid="submit-page">
      <div className="grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="eyebrow mb-6">For authors</p>
          <h1 className="f-serif text-5xl sm:text-6xl tracking-tight leading-[0.95]"><MaskLines lines={["Submit a", <em key="m" className="font-light">manuscript.</em>]} /></h1>
          <div className="mt-12 space-y-6 text-[var(--ink-2)] leading-relaxed lg:sticky lg:top-28">
            {[["Scope", "Empirical and conceptual work on firm performance: strategy, finance, leadership, operations, technology and macroeconomics."], ["Formats", "Research articles (6,000–10,000 words), practitioner essays (2,500–4,000) and teaching case studies."], ["Review", "Double-blind peer review. Remove identifying details from the manuscript file. First decision typically in 6–8 weeks."]].map(([t, d]) => (
              <div key={t} className="border-t rule pt-4"><p className="f-serif text-xl text-[var(--ink)]">{t}</p><p className="mt-1">{d}</p></div>
            ))}
          </div>
        </div>
        <form onSubmit={submit} className="lg:col-span-7 space-y-8" data-testid="manuscript-form">
          <Field label="Manuscript title *"><input required minLength={3} value={f.title} onChange={set("title")} className="field f-serif !text-2xl" data-testid="ms-title-input" /></Field>
          <div className="grid sm:grid-cols-2 gap-8">
            <Field label="Author(s) *"><input required value={f.authors} onChange={set("authors")} className="field" placeholder="Jane Doe, John Roe" data-testid="ms-authors-input" /></Field>
            <Field label="Corresponding email *"><input required type="email" value={f.email} onChange={set("email")} className="field" data-testid="ms-email-input" /></Field>
            <Field label="Affiliation"><input value={f.affiliation} onChange={set("affiliation")} className="field" data-testid="ms-affiliation-input" /></Field>
            <Field label="ORCID iD (optional)"><input value={f.orcid} onChange={set("orcid")} pattern="^$|^\d{4}-\d{4}-\d{4}-\d{3}[\dX]$" title="Format 0000-0000-0000-0000" className="field f-mono" placeholder="0000-0000-0000-0000" data-testid="ms-orcid-input" /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-8">
            <Field label="Article type"><select value={f.manuscript_type} onChange={set("manuscript_type")} className="field" data-testid="ms-type-select">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
            <Field label="Subject area"><select value={f.category} onChange={set("category")} className="field" data-testid="ms-category-select">{CATS.map((t) => <option key={t}>{t}</option>)}</select></Field>
          </div>
          <Field label={`Abstract * (${f.abstract.length} chars)`}><textarea required rows={6} value={f.abstract} onChange={set("abstract")} className="field resize-none" data-testid="ms-abstract-input" /></Field>
          <Field label="Keywords"><input value={f.keywords} onChange={set("keywords")} className="field" placeholder="productivity, digital transformation" data-testid="ms-keywords-input" /></Field>
          <label className="block border border-dashed border-[var(--ink)]/40 hover:border-[var(--ink)] transition-colors p-8 text-center cursor-pointer" data-testid="ms-file-dropzone">
            <UploadCloud className="mx-auto text-[var(--muted)]" />
            <p className="mt-3 font-medium">{file ? file.name : "Upload manuscript (PDF, DOC, DOCX · max 10 MB)"}</p>
            <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ms-file-input" />
          </label>
          <button disabled={busy} className="btn-red h-13 py-4 w-full sm:w-auto px-10 font-semibold disabled:opacity-50" data-testid="manuscript-submit-btn">{busy ? "Submitting…" : "Submit for review"}</button>
        </form>
      </div>
    </div>
  );
}
